/** Color conversion helpers for the Figma-style color picker.
 *  Values persist as hex (#rrggbb / #rrggbbaa) or the keyword 'transparent'
 *  — the formats already stored in template documents. */

const clamp01 = (v) => Math.min(1, Math.max(0, v));

export function parseColor(input) {
    if (!input || input === 'transparent') {
        return { r: 0, g: 0, b: 0, a: input === 'transparent' ? 0 : 1 };
    }
    const str = String(input).trim();

    const hex = str.replace(/^#/, '');
    if (/^[0-9a-f]{3,8}$/i.test(hex)) {
        if (hex.length === 3 || hex.length === 4) {
            const r = parseInt(hex[0] + hex[0], 16);
            const g = parseInt(hex[1] + hex[1], 16);
            const b = parseInt(hex[2] + hex[2], 16);
            const a = hex.length === 4 ? parseInt(hex[3] + hex[3], 16) / 255 : 1;
            return { r, g, b, a };
        }
        if (hex.length === 6 || hex.length === 8) {
            const r = parseInt(hex.slice(0, 2), 16);
            const g = parseInt(hex.slice(2, 4), 16);
            const b = parseInt(hex.slice(4, 6), 16);
            const a = hex.length === 8 ? parseInt(hex.slice(6, 8), 16) / 255 : 1;
            return { r, g, b, a };
        }
    }

    const rgba = /^rgba?\(([^)]+)\)$/i.exec(str);
    if (rgba) {
        const parts = rgba[1].split(',').map((p) => parseFloat(p.trim()));
        return {
            r: Math.round(parts[0] || 0),
            g: Math.round(parts[1] || 0),
            b: Math.round(parts[2] || 0),
            a: parts[3] != null && Number.isFinite(parts[3]) ? clamp01(parts[3]) : 1,
        };
    }

    return null;
}

const toHex2 = (v) => Math.round(Math.min(255, Math.max(0, v))).toString(16).padStart(2, '0');

/** rgba → '#rrggbb' (a=1) or '#rrggbbaa' */
export function rgbaToHex({ r, g, b, a = 1 }) {
    const base = `#${toHex2(r)}${toHex2(g)}${toHex2(b)}`;
    if (a >= 1) return base;
    return `${base}${toHex2(a * 255)}`;
}

export function rgbToHsv({ r, g, b }) {
    const rn = r / 255;
    const gn = g / 255;
    const bn = b / 255;
    const max = Math.max(rn, gn, bn);
    const min = Math.min(rn, gn, bn);
    const d = max - min;
    let h = 0;
    if (d !== 0) {
        if (max === rn) h = ((gn - bn) / d) % 6;
        else if (max === gn) h = (bn - rn) / d + 2;
        else h = (rn - gn) / d + 4;
        h *= 60;
        if (h < 0) h += 360;
    }
    const s = max === 0 ? 0 : d / max;
    return { h, s, v: max };
}

export function hsvToRgb({ h, s, v }) {
    const c = v * s;
    const hp = ((h % 360) + 360) % 360 / 60;
    const x = c * (1 - Math.abs((hp % 2) - 1));
    let rn = 0;
    let gn = 0;
    let bn = 0;
    if (hp < 1) [rn, gn, bn] = [c, x, 0];
    else if (hp < 2) [rn, gn, bn] = [x, c, 0];
    else if (hp < 3) [rn, gn, bn] = [0, c, x];
    else if (hp < 4) [rn, gn, bn] = [0, x, c];
    else if (hp < 5) [rn, gn, bn] = [x, 0, c];
    else [rn, gn, bn] = [c, 0, x];
    const m = v - c;
    return {
        r: Math.round((rn + m) * 255),
        g: Math.round((gn + m) * 255),
        b: Math.round((bn + m) * 255),
    };
}

/** Display helper — '#RRGGBB' without alpha for hex inputs */
export function hexNoAlpha(color) {
    const rgba = parseColor(color);
    if (!rgba) return '000000';
    return rgbaToHex({ ...rgba, a: 1 }).slice(1).toUpperCase();
}

export function alphaPercent(color) {
    const rgba = parseColor(color);
    if (!rgba) return 100;
    return Math.round(rgba.a * 100);
}
